<script setup lang="ts">
import emptyImage from '@/assets/images/empty_list.png'
import ScannedWineCard from '@/components/ScannedWineCard.vue'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import { useWineStore } from '@/stores/wine'
import { ScannedWine } from '@/types'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()
const authStore = useAuthStore()
const uiStore = useUiStore()
const wineStore = useWineStore()

const isLoading = ref(false)
const isDev = computed(
    () => (import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV === true
)
const displayedWines = computed<ScannedWine[]>(() => wineStore.scannedWines)

// Dev Telegram login
const isLoginLoading = ref(false)

// Test Telegram user data for development
const testTelegramUser = {
    telegram_id: 123456789,
    username: 'testuser',
    first_name: 'Test',
    last_name: 'User',
    language_code: 'ru',
    is_premium: false,
    photo_url: 'https://via.placeholder.com/150',
    email: 'test@example.com',
    init_data: isDev.value
        ? `user=%7B%22id%22%3A123456789%2C%22first_name%22%3A%22Test%22%2C%22last_name%22%3A%22User%22%2C%22username%22%3A%22testuser%22%2C%22language_code%22%3A%22ru%22%2C%22is_premium%22%3Afalse%7D&auth_date=${Math.floor(Date.now() / 1000)}&hash=mock_hash_for_dev`
        : undefined,
}

const testTelegramLogin = async () => {
    isLoginLoading.value = true
    try {
        const result = await authStore.loginWithTelegramUser(testTelegramUser)
        if (result.success) {
            console.log('✅ Test Telegram login successful')
            // Reload wines after successful login
            await loadWines()
        } else {
            console.error('❌ Test Telegram login failed:', result.error)
            alert(`Telegram login failed: ${result.error}`)
        }
    } catch (error) {
        console.error('❌ Test Telegram login error:', error)
        alert(`Telegram login error: ${error}`)
    } finally {
        isLoginLoading.value = false
    }
}

const devLogout = async () => {
    await authStore.logout()
    wineStore.scannedWines = []
}

const goToWine = (scanId: string) => {
    router.push({ name: 'wine-info', params: { id: scanId } })
}

const loadWines = async () => {
    if (isLoading.value) return

    uiStore.beginDataLoad()
    isLoading.value = true
    console.log('🍷 Loading scanned wines...')
    console.log('🔐 Auth status:', authStore.isAuthenticated, 'User:', authStore.user)

    try {
        const result = await wineStore.loadScans({ page: 1, limit: 50 })

        if (result.success) {
            console.log('✅ Loaded scanned wines:', wineStore.scannedWines.length)
        } else {
            console.log('❌ Failed to load scans:', result.error)
        }
    } catch (error) {
        console.error('Failed to load scanned wines:', error)
    } finally {
        isLoading.value = false
        uiStore.endDataLoad()
    }
}

onMounted(async () => {
    // Дождаться завершения инициализации auth store
    while (authStore.isLoading) {
        await new Promise((resolve) => setTimeout(resolve, 50))
    }

    // Теперь загружаем данные
    setTimeout(loadWines, 100)
})
</script>

<template>
    <section class="my-wines">
        <h1 class="my-wines__title">Мои вина</h1>

        <div v-if="!displayedWines.length" class="empty">
            <div class="empty__wrap">
                <img :src="emptyImage" alt="Пусто" class="empty__img" />
                <div class="empty__text-wrap">
                    <div class="empty__title">Список вин пуст</div>
                    <div class="empty__desc">
                        Здесь будут храниться все отсканированные вина и&nbsp;ваши
                        отзывы&nbsp;о&nbsp;них
                    </div>
                </div>
            </div>
            <RouterLink to="/scan" class="btn empty__btn">Сканируйте ваше первое вино</RouterLink>
        </div>

        <div v-else class="my-wines__grid">
            <ScannedWineCard
                v-for="scannedWine in displayedWines"
                :key="scannedWine.id"
                :scannedWine="scannedWine"
                @click="goToWine(scannedWine.id)"
            />
        </div>

        <!-- Dev auth controls -->
        <div
            v-if="isDev"
            class="dev-auth"
            style="
                margin-bottom: 16px;
                padding: 12px;
                border: 1px solid var(--app-border-color);
                border-radius: 8px;
                background: var(--app-bg-secondary);
            "
        >
            <div v-if="!authStore.isAuthenticated">
                <div style="margin-bottom: 16px; font-weight: 600">
                    Для просмотра сканов нужна авторизация:
                </div>

                <button
                    class="btn"
                    type="button"
                    @click="testTelegramLogin"
                    :disabled="isLoginLoading"
                    style="background: var(--app-info); margin-bottom: 8px"
                >
                    {{ isLoginLoading ? 'Вход...' : 'Войти через Telegram (Тест)' }}
                </button>
                <div style="color: var(--app-text-muted); font-size: 14px">Для разработки</div>
            </div>

            <div v-else>
                <span style="color: var(--app-success); font-weight: 600"
                    >✓ Авторизован как: {{ authStore.user?.username }} ({{
                        authStore.user?.role
                    }})</span
                >
                <button
                    class="btn"
                    type="button"
                    @click="devLogout"
                    style="background: var(--app-danger); color: white"
                >
                    Выйти
                </button>
            </div>
        </div>
    </section>
</template>

<style scoped lang="stylus">
.my-wines
    display flex
    flex-direction column
    gap 24px
    padding 24px 16px
    &__title
        font-size 28px
        line-height 32px
        font-weight 600
        color var(--app-text-color)

    &__grid
        display flex
        flex-direction column
        gap 24px

.empty
    min-height calc(100vh - var(--app-footer-height) - 105px)
    display flex
    flex-direction column
    align-items center
    justify-content space-between
    gap 12px
    padding-top 44px
    text-align center
    &__title
        font-size 22px
        line-height 26px
        font-weight 700
        color var(--app-text-color)
    &__img
        width 50%
        min-width 180px
        height auto
        display block
        margin 0 auto 24px
    &__title
        margin-bottom 4px
        font-size 24px
        line-height 36px
        font-weight 600
        color var(--app-text-color)
    &__desc
        max-width 420px
        margin 0 auto
        opacity .8
        font-size 16px
        line-height 24px
        color var(--app-text-color)
    &__btn
        margin-top 8px
    &__dev
        margin-top 16px
        width 100%
</style>
